/**
 * referralDashboardAdapter — `ReferralCodeResource` → `UIReferralDashboard`.
 *
 * Portage littéral de `src/admin/ReferralCodeScreen.js` : les trois compteurs y sont lus avec un
 * repli à zéro (`:602`, `:626`, `:650`), et le bonus s'écrit en collant un « € » au montant reçu
 * (`:361` pour l'étape 3, `:673-676` pour la tuile).
 *
 * Le montant est **interpolé tel que le backend le sert**, comme le legacy. `commission_amount` est
 * une colonne `double(8,2) NOT NULL` (`database/schema/mysql-schema.sql`), donc un nombre JSON :
 * `50` s'écrit « 50€ ». Imposer ici un format (deux décimales, séparateur) ferait dire deux choses
 * différentes aux deux chemins du drapeau pour la même donnée.
 *
 * La modale de partage lit la même charge utile (`useReferralShareAdapter`) : le type brut est
 * partagé, il n'est pas redéclaré ici.
 */
import { type ReferralCodePayload } from "../../../ui/types/referralCode"
import { type UIReferralDashboard } from "../../../ui/types"
import { euros, eurosCents } from "../../../ui/formatters/moneyFormatter"

/**
 * `null` quand il n'y a pas de lien à partager : une page de parrainage sans lien n'a rien à dire,
 * et l'appelant montre alors l'erreur de la tranche api plutôt qu'une carte vide.
 */
export function toUIReferralDashboard(payload: ReferralCodePayload | null | undefined): UIReferralDashboard | null {
    const url = payload?.referee_url
    if (!url) return null

    const rewardLabel = referralRewardLabel(payload?.commission_amount)

    return {
        url,
        rewardLabel,
        stats: {
            clicks: counterOf(payload?.clicks_count),
            accountsCreated: counterOf(payload?.accounts_created_count),
            activatedReferrals: counterOf(payload?.activated_referrals_count),
            rewardPerReferralLabel: rewardLabel,
        },
    }
}

/**
 * « Marie Lefevre - marie.lefevre@example.com », mot pour mot la ligne de `:162-165`.
 *
 * Elle n'a de sens que sur la vue ouverte par lien : c'est l'appelant qui décide de la demander,
 * pas la charge utile — le cœur du header parent passe le hash du parent lui-même.
 */
export function toReferralReferrerLabel(payload: ReferralCodePayload | null | undefined): string | null {
    const referrer = payload?.referrer
    if (referrer == null) return null

    const name = [referrer.firstname, referrer.lastname].filter((part) => !!part).join(" ")
    const parts = [name, referrer.email].filter((part) => !!part)
    return parts.length > 0 ? parts.join(" - ") : null
}

/**
 * Ce qu'on partage : le message tout prêt du backend, à défaut le lien seul.
 *
 * Portage de `SocialShareButtons.js:25` (`message || url`), que la page legacy monte avec
 * `sharing_message` et `referee_url`. Le backend ne sert `sharing_message` que si la relation
 * `referrer` est chargée (`ReferralCodeResource.php:48`) : les deux appels de cette page la
 * chargent, mais la règle de repli vit ici plutôt que dans le crochet, qui n'a pas à connaître les
 * noms de champs de l'API.
 */
export function referralShareContent(payload: ReferralCodePayload | null | undefined): string {
    return payload?.sharing_message || payload?.referee_url || ""
}

/**
 * Le legacy écrit `${commission_amount}€` sans garde : un montant absent y donne « undefined€ ».
 * La colonne étant NOT NULL, le repli est inobservable — on écrit une chaîne vide plutôt qu'un mot
 * inventé. Zéro reste un montant : `0` s'écrit « 0€ », comme dans le legacy.
 *
 * Exporté pour le bandeau de parrainage de l'accueil, qui doit écrire le gain comme la page.
 */
export function referralRewardLabel(amount: number | string | null | undefined): string {
    if (amount == null || amount === "") return ""
    // Le montant arrive en nombre ou en chaîne décimale (« 50.00 ») : écrit comme tout prix à une
    // famille (`moneyFormatter`) : l'euro entier quand il tombe juste, les centimes sinon — une
    // commission de 62,50 € ne s'arrondit pas.
    const value = typeof amount === "number" ? amount : Number(amount)
    if (!Number.isFinite(value)) return ""
    return Number.isInteger(value) ? euros(value) : eurosCents(Math.round(value * 100))
}

/** `clicks_count || 0` du legacy : une clé absente, `null` ou `0` comptent pour zéro. */
function counterOf(value: number | null | undefined): number {
    return typeof value === "number" && Number.isFinite(value) ? value : 0
}
